/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import orderBy from 'lodash.orderby';
import { LuisId } from 'src/luis/types/Resource';

export type SortingProps<T> = {
    property: keyof T;
    order: 'asc' | 'desc';
};

export type PageInfo = {
    skip: number;
    take: number;
};

export type PaginationFactoryParams<T> = {
    pages: Map<number, Map<LuisId, T>>;
    totalSize: number;
    pageSize: number;
    activePageIndex: number;
    sortingProps: SortingProps<T>;
};

/**
 * This method makes sure that the shape of the cache is always intact; it does so by carrying out two processes.
 * 1) Distribute
 * 2) Reconcile
 *
 * @returns A pageInfo object that represent the page that should refreshed from the backend.
 */
export const defragment = <T>(params: PaginationFactoryParams<T>): PageInfo | void => {
    const { pageSize, pages, totalSize, activePageIndex, sortingProps } = params;
    const numOfPages = Math.max(pages.size, Math.ceil(totalSize / pageSize));
    let currentSize = 0;
    for (const [, page] of pages) {
        currentSize += page.size;
    }

    /**
     * Distributes the data evenly between the pages so that no page can ever exceed the page size.
     */
    const distribute = () => {
        let sizeDiff = NaN;
        for (let i = 0; i < numOfPages; i += 1) {
            const page = pages.get(i);
            const prevPage = pages.get(i - 1);
            const nextPage = pages.get(i + 1);

            sizeDiff = calculateSizeDiff(sizeDiff, prevPage);

            if (activePageIndex === i && page !== undefined && page.size !== pageSize) {
                sizeDiff = pageSize - page.size;
            }

            let toBeSortedPage: Map<LuisId, T>;
            if (sizeDiff > 0) {
                if (activePageIndex !== i) {
                    toBeSortedPage = shiftElements(page, prevPage, sizeDiff);
                }
            } else if (sizeDiff < 0) {
                toBeSortedPage = shiftElements(page, nextPage, sizeDiff);
            }

            if (toBeSortedPage) {
                sortMap(toBeSortedPage, sortingProps);
            }
        }
    };

    /**
     * Returns the max absolute value between the old size difference, the difference between the page size
     * and previous page.
     * This is used when the size difference between pages is not uniform, this happens when the user deletes
     * items from different pages in one batch operation (when filters or search are applied).
     */
    const calculateSizeDiff = (oldSizeDiff: number, prevPage: Map<LuisId, T>): number => {
        if (isNaN(oldSizeDiff)) {
            return NaN;
        }
        const prevPageSizeDiff = (prevPage && pageSize - prevPage.size) || 0;
        const absSizeDifference = Math.abs(oldSizeDiff);
        const absPrevPageSizeDiff = Math.abs(prevPageSizeDiff);

        return absSizeDifference > absPrevPageSizeDiff ? oldSizeDiff : prevPageSizeDiff;
    };

    /**
     * Reconciles the cache to an acceptable state.
     * The rules are:
     * - All pages must have the page size if not they must be deleted.
     * - If the active page size doesn't match the page size, then the method should return the active page index
     * and it's the responsibility of the caller to refresh that page.
     * - If the chunking process is running, then it's guaranteed (giving that the invalidation ran
     * before the start of the chunking process) that only the last fetched page might have missing elements in that case,
     * the method returns the last fetched page index and it's the responsibility of the caller to refresh that page.
     *
     * @param _activePageIndex The index of the active page.
     */
    const reconcile = (): void => {
        let accumulatedSize = 0;
        for (const [pageIndex, page] of pages) {
            accumulatedSize += page.size;

            if (pageIndex === activePageIndex) {
                continue;
            }

            // TODO: If the last page has 10 items and user adds a new utterance the new last page is already fetched
            if (page.size !== pageSize && accumulatedSize < totalSize) {
                pages.delete(pageIndex);
            }
        }
    };

    distribute();

    // Skip refreshing if the active page is the last one, except for if the last page is the first page.
    if (activePageIndex !== 0 && activePageIndex === numOfPages - 1) {
        return undefined;
    }

    reconcile();

    const activePage = pages.get(activePageIndex);

    if ((activePage === undefined || activePage.size !== pageSize) && currentSize !== totalSize) {
        return { skip: activePageIndex * pageSize, take: pageSize };
    }

    return undefined;
};

/**
 * Sorts page's data in-place, according to the sorting props provided in the constructor.
 *
 * @param page The page to be sorted.
 */
export const sortMap = <T>(page: Map<LuisId, T>, sortingProps: SortingProps<T>): void => {
    // Reverse the sorted array so that it gets inserted in the map in the correct order
    const data = orderBy([...page.entries()], [sortingProps.property], [sortingProps.order]).reverse();

    page.clear();
    data.forEach(([key, model]) => page.set(key, model));
};

/**
 * Shifts some elements from one map to another.
 *
 * @param fromArr The providing map.
 * @param toArr The receiving map.
 * @param count The total number of items to be shifted
 *
 * @returns The receiving map.
 */
const shiftElements = <T>(fromArr: Map<LuisId, T>, toArr: Map<LuisId, T>, count: number): Map<LuisId, T> => {
    if (fromArr === undefined) {
        return;
    }
    const data = count < 0 ? [...fromArr.entries()] : [...fromArr.entries()].reverse();
    let absCount = Math.abs(count);
    for (const [key, model] of data) {
        if (absCount === 0) {
            return toArr;
        }
        if (toArr !== undefined) {
            toArr.set(key, model);
        }
        fromArr.delete(key);
        absCount -= 1;
    }

    return toArr;
};
